// Chapters screen — ported from Claude Design/app/chapters.jsx
// Adapted to react-router + API.

import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useStore } from '../state/store.js';
import { VideoThumb, Icons, StatusLabel, ruPlural } from '../components/ui.jsx';
import { formatDurationHm, formatBytes } from '../lib/format.js';

export function ChaptersScreen() {
  const { epId } = useParams();
  const navigate = useNavigate();
  const toast = useStore((s) => s.toast);

  const [episode, setEpisode] = useState(null);
  const [open, setOpen] = useState(() => new Set());
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    api.getEpisode(epId)
      .then((ep) => {
        setEpisode(ep);
        // Open the first chapter by default
        if (ep?.chapters?.length) setOpen(new Set([ep.chapters[0].id]));
      })
      .catch((err) => toast(err.message, 'err'));
  }, [epId, toast]);

  if (!episode) {
    return <div className="screen"><div className="empty-state"><div className="empty-icon">◇</div><div className="empty-title">загрузка…</div></div></div>;
  }

  const chapters = episode.chapters || [];
  const allClips = chapters.flatMap((c) => c.clips || []);
  const counts = {
    total: allClips.length,
    published: allClips.filter((c) => c.status === 'published').length,
    queued: allClips.filter((c) => c.status === 'queued').length,
    draft: allClips.filter((c) => c.status === 'draft').length,
    failed: allClips.filter((c) => c.status === 'failed').length,
  };

  const toggle = (id) => setOpen((cur) => {
    const n = new Set(cur);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  return (
    <div className="screen">
      <div className="breadcrumb">
        <button className="back" onClick={() => navigate('/episodes')}>← эпизоды</button>
        <span className="crumb-sep">/</span>
        <span className="crumb-current crumb-strong">{episode.title}</span>
        <span className="crumb-meta">
          {episode.title_ru ? `${episode.title_ru} · ` : ''}
          {formatDurationHm(episode.duration_seconds)}
          {' · '}{chapters.length} тем · {allClips.length} фрагментов
        </span>
        <div className="grow" />
      </div>

      <SourceStrip
        episode={episode}
        chapters={chapters}
        onJumpToClip={(c) => navigate(`/clip/${c.id}`)}
      />

      <div className="screen-header screen-header-compact" style={{ paddingTop: 16 }}>
        <h1 className="page-title">Темы</h1>
        <span className="page-sub">
          <b>{chapters.length}</b> {ruPlural(chapters.length, 'тема', 'темы', 'тем')} · <b>{counts.total}</b> фрагментов
        </span>
        <div className="grow" />
        <div className="screen-header-actions">
          <select className="select" value={filter} onChange={(e) => setFilter(e.target.value)} title="фильтр по статусу">
            <option value="all">все · {counts.total}</option>
            <option value="published">опубликованные · {counts.published}</option>
            <option value="queued">в очереди · {counts.queued}</option>
            <option value="draft">черновики · {counts.draft}</option>
          </select>
          <button className="btn btn-ghost btn-icon" onClick={() => setOpen(new Set(chapters.map((c) => c.id)))} title="развернуть все темы">⤓</button>
          <button className="btn btn-outline" onClick={() => navigate(`/episode/${epId}`)}>← транскрипт</button>
          <button className="btn btn-dark" onClick={() => navigate(`/episode/${epId}/new-clip`)}>+ клип</button>
        </div>
      </div>

      <div className="chapters">
        {chapters.map((c, idx) => (
          <Chapter key={c.id} c={c} idx={idx} open={open.has(c.id)} onToggle={() => toggle(c.id)}
                   filter={filter} onClip={(s) => navigate(`/clip/${s.id}`)} />
        ))}
      </div>
    </div>
  );
}

function SourceStrip({ episode, chapters, onJumpToClip }) {
  const totalSec = Math.max(1, episode.duration_seconds || 1);
  const ticks = [0, 0.2, 0.4, 0.6, 0.8, 1].map((f) => ({
    f,
    label: formatTickLabel(f * totalSec),
  }));
  return (
    <div className="source-strip">
      <div className="source-strip-head">
        <span className="chip chip-dark">grabación original</span>
        <span className="source-strip-meta">
          00:00 — {formatDurationHm(episode.duration_seconds)} · {chapters.length} тем
        </span>
        <div className="grow" />
        <span className="muted small">наведи на полоску — превью</span>
      </div>
      <div className="source-strip-bar">
        {chapters.map((c, i) => {
          const left = c.t_start / totalSec;
          const next = chapters[i + 1];
          const right = next ? (next.t_start / totalSec) : Math.min(1, c.t_end / totalSec);
          const w = Math.max(0.02, right - left);
          return (
            <div key={c.id} className="src-range" style={{ left: (left * 100) + '%', width: (w * 100) + '%' }} title={c.title}>
              <span className="src-range-label">{c.title}</span>
              {(c.clips || []).map((s) => {
                const sl = s.t_start / totalSec;
                const sLeft = (sl - left) / w;
                return (
                  <div key={s.id} className="src-slice" style={{ left: Math.max(0, Math.min(1, sLeft)) * 100 + '%' }}
                       onClick={(e) => { e.stopPropagation(); onJumpToClip(s); }} title={s.title} />
                );
              })}
            </div>
          );
        })}
      </div>
      <div className="source-strip-ticks">
        {ticks.map((t, i) => (
          <span key={i} style={{ left: (t.f * 100) + '%' }}>{t.label}</span>
        ))}
      </div>
    </div>
  );
}

function formatTickLabel(seconds) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}`;
  return `0:${String(m).padStart(2, '0')}`;
}

function Chapter({ c, idx, open, onToggle, filter, onClip }) {
  const clips = (c.clips || []).filter((s) =>
    filter === 'all' ? true : s.status === filter,
  );
  const roman = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X'][idx] || (idx + 1);
  const chapterStart = formatChapterTime(c.t_start);
  const chapterDur = formatChapterDur(c.t_end - c.t_start);

  return (
    <section className={'chapter' + (open ? ' chapter-open' : '')}>
      <header className="chapter-head" onClick={onToggle}>
        <span className="chev">{open ? '▾' : '▸'}</span>
        <span className="chapter-roman">{roman}</span>
        <div className="chapter-title-block">
          <div className="chapter-title-es">{c.title}</div>
          {c.title_ru && <div className="chapter-title-ru">{c.title_ru}</div>}
        </div>
        <span className="chip chip-soft">{chapterStart} · {chapterDur}</span>
        <span className="chip chip-soft">{clips.length} {ruPlural(clips.length, 'фрагмент', 'фрагмента', 'фрагментов')}</span>
        <span className="grow" />
        {c.summary && <span className="chapter-summary">{c.summary}</span>}
      </header>
      <div className="chapter-body" style={{ maxHeight: open ? 1400 : 0 }}>
        <div className="story-grid">
          {clips.length === 0
            ? <div className="empty">по фильтру ничего нет</div>
            : clips.map((s, i) => <ClipCard key={s.id} s={s} num={i + 1} onClick={() => onClip(s)} />)}
        </div>
      </div>
    </section>
  );
}

function formatChapterTime(seconds) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  return `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`;
}

function formatChapterDur(seconds) {
  const m = Math.floor(seconds / 60);
  return `${m}м`;
}

function ClipCard({ s, num, onClick }) {
  const start = formatChapterTime(s.t_start);
  const dur = formatChapterDur(s.t_end - s.t_start);
  return (
    <div className="story-card" onClick={onClick} role="button" tabIndex="0">
      <div className="story-thumb">
        <VideoThumb aspect="9/16" src={s.thumbnail_url} label={`#${num}`} />
        <span className="badge badge-dark">{start}</span>
        <span className="badge badge-light">⏱ {dur}</span>
        <button className="play-pill" onClick={(e) => { e.stopPropagation(); onClick(); }}>{Icons.play}</button>
      </div>
      <div className="story-body">
        <div className="story-title-es">{s.title}</div>
        {s.title_ru && <div className="story-title-ru">{s.title_ru}</div>}
        <div className="story-meta">{s.subtitles_count || 0} строк · <StatusLabel status={s.status} /></div>
      </div>
    </div>
  );
}
